import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useChowaTheme } from '../config/chowaThemeContext';
import { getChowaSeries, createChowaSeries, updateChowaSeries, deleteChowaSeries } from '../api_config';

const SeriesManagement = () => {
  const { getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ダイアログ状態
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [editingSeries, setEditingSeries] = useState(null);
  const [seriesName, setSeriesName] = useState('');
  const [dialogError, setDialogError] = useState('');

  // 全角文字チェック関数
  const hasFullWidthChars = (str) => {
    if (!str) return false;
    // eslint-disable-next-line no-control-regex
    return /[^\x00-\x7F]/.test(str);
  };

  // バリデーション関数
  const isSeriesNameValid = () => {
    return seriesName.trim() && !hasFullWidthChars(seriesName.trim());
  };

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getChowaSeries();
      if (response.data.status === 'success') {
        setSeries(response.data.data.series);
      } else {
        setError('シリーズデータの取得に失敗しました');
      }
    } catch (err) {
      console.error('シリーズ取得エラー:', err);
      setError('シリーズデータの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setDialogMode('create');
    setEditingSeries(null);
    setSeriesName('');
    setDialogError('');
    setDialogOpen(true);
  };

  const handleEdit = (seriesItem) => {
    setDialogMode('edit');
    setEditingSeries(seriesItem);
    setSeriesName(seriesItem.name);
    setDialogError('');
    setDialogOpen(true);
  };

  const handleDelete = async (seriesItem) => {
    if (window.confirm(`シリーズ「${seriesItem.name}」を削除しますか？`)) {
      try {
        const response = await deleteChowaSeries(seriesItem.id);
        if (response.data.status === 'success') {
          await fetchSeries(); // 一覧を再取得
        } else {
          alert(response.data.message || '削除に失敗しました');
        }
      } catch (err) {
        console.error('削除エラー:', err);
        if (err.response && err.response.data && err.response.data.message) {
          alert(err.response.data.message);
        } else {
          alert('削除中にエラーが発生しました');
        }
      }
    }
  };

  const handleDialogSave = async () => {
    if (!seriesName.trim()) {
      setDialogError('シリーズ名を入力してください');
      return;
    }

    if (hasFullWidthChars(seriesName.trim())) {
      setDialogError('半角文字のみ入力してください');
      return;
    }

    try {
      setDialogError('');
      let response;
      
      if (dialogMode === 'create') {
        response = await createChowaSeries({ name: seriesName.trim() });
      } else {
        response = await updateChowaSeries(editingSeries.id, { name: seriesName.trim() });
      }

      if (response.data.status === 'success') {
        setDialogOpen(false);
        await fetchSeries(); // 一覧を再取得
      } else {
        setDialogError(response.data.message || '保存に失敗しました');
      }
    } catch (err) {
      console.error('保存エラー:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setDialogError(err.response.data.message);
      } else {
        setDialogError('保存中にエラーが発生しました');
      }
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogError('');
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            データを読み込み中...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={fetchSeries}
            sx={{ backgroundColor: currentTheme.primary }}
          >
            再試行
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            シリーズ管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSeries}
              sx={{ 
                color: currentTheme.primary, 
                borderColor: currentTheme.primary,
                '&:hover': {
                  borderColor: currentTheme.primary,
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              sx={{ 
                backgroundColor: currentTheme.primary,
                '&:hover': {
                  backgroundColor: currentTheme.primaryDark
                }
              }}
            >
              新規シリーズ
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          登録されているシリーズ: {series.length}件
        </Typography>

        {series.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              シリーズが登録されていません
            </Typography>
            <Typography variant="body2" color="text.secondary">
              「新規シリーズ」ボタンから追加してください
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>シリーズ名</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {series.map((seriesItem) => (
                  <TableRow key={seriesItem.id}>
                    <TableCell>{seriesItem.id}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary'
                        }}
                      >
                        {seriesItem.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(seriesItem)}
                        sx={{ color: currentTheme.primary }}
                        title="編集"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(seriesItem)}
                        sx={{ color: 'error.main' }}
                        title="削除"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* 新規作成・編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? '新規シリーズ作成' : 'シリーズ編集'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="シリーズ名"
            type="text"
            fullWidth
            variant="outlined"
            value={seriesName}
            onChange={(e) => setSeriesName(e.target.value)}
            placeholder="例: HV-300, HV-600"
            error={!!dialogError || (seriesName.trim() && hasFullWidthChars(seriesName.trim()))}
            helperText={
              dialogError || 
              (seriesName.trim() && hasFullWidthChars(seriesName.trim()) ? '半角文字のみ入力してください' : '機種シリーズ名を入力してください（半角文字のみ）')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDialogSave} 
            variant="contained"
            disabled={!isSeriesNameValid()}
            sx={{ 
              backgroundColor: isSeriesNameValid() ? currentTheme.primary : 'grey.400',
              '&:hover': {
                backgroundColor: isSeriesNameValid() ? currentTheme.primaryDark : 'grey.400'
              },
              '&.Mui-disabled': {
                backgroundColor: 'grey.400',
                color: 'grey.600'
              }
            }}
          >
            {dialogMode === 'create' ? '作成' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SeriesManagement;
